import React, { useState, ReactElement } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { spacing, getColor, type, getTextColor, getInputTheme } from '../style';
import Checkmark from './icons/Checkmark';
import { CheckboxV2 } from 'swui';
import { ILabelTextProps } from './types/Checkbox';
import { CustomStyledProps } from 'style/style';

const Label = styled('label')(
  () => ({
    display: 'block',
    cursor: 'pointer',
    ...spacing.MARGIN_TOP_SM,
    ...spacing.MARGIN_BOTTOM_SM,
    ...spacing.MARGIN_RIGHT_MD,
    ...getTextColor().NORMAL,
  }),
  ({ inline }: CustomStyledProps) => inline && { display: 'inline-block' },
  ({ isLogin }: CustomStyledProps) => isLogin && { color: 'rgba(0,0,0,0.6)' },
  ({ noSpace }: CustomStyledProps) =>
    noSpace && {
      margin: 0,
    },

  ({ customStyle }) =>
    customStyle && {
      ...customStyle,
    }
);

const CheckboxInput = styled(CheckboxV2)({
  position: 'absolute',
  width: '0',
  opacity: '0',
});

type CheckboxStyledProps = {
  focused?: boolean;
  checked?: number | string;
  hasError?: boolean;
  customStyle?: React.CSSProperties;
};

const StyledCheckbox = styled.div<CheckboxStyledProps>`
  background: ${getInputTheme().backgroundInput};
  border: 1px solid #dbdbdb;
  border-radius: 3px;
  height: 18px;
  width: 18px;
  display: inline-block;
  text-align: center;
  overflow: hidden;
  vertical-align: middle;

  ${({ focused }) => focused && `border-color: ${getColor().BRAND_1};`}
  ${({ checked }) =>
    checked &&
    `
    background-color: ${getInputTheme().checkedInput};
    border-color: ${getColor().BRAND_1};
  `}
  ${({ hasError }) =>
    hasError &&
    `
    border: 1px solid #cc5453;
  `}
  ${({ customStyle }) => customStyle && { ...customStyle }}
`;

const CheckmarkContainer = styled('div')({
  position: 'relative',
  margin: '-7px',
  left: '-1px',
});

const LabelText = styled('span')<ILabelTextProps>(
  {
    ...spacing.PADDING_LEFT_SM,
  },
  ({ disabled }) =>
    disabled && {
      color: '#cccccc',
    },
  ({ size }) => (size ? { ...type[size] } : { ...type['SM'] }),
  ({ customLabelTextStyle }) =>
    customLabelTextStyle && {
      ...customLabelTextStyle,
    }
);

interface CheckboxProps {
  disabled?: boolean;
  label?: React.ReactNode;
  inline?: boolean;
  noSpace?: boolean;
  size?: 'SM' | 'MD';
  hasError?: boolean;
  value: any;
  customStyle?: React.CSSProperties;
  customLabelTextStyle?: React.CSSProperties;
  isLogin?: boolean;
  svgCustomStyle?: React.CSSProperties;
  labelCustomStyle?: React.CSSProperties;
  onChange?: (e?: any) => void;
  id?: string;
  type?: string;
}

const Checkbox = ({
  disabled,
  label,
  inline,
  noSpace,
  size,
  hasError,
  value,
  customStyle,
  customLabelTextStyle = {},
  svgCustomStyle = {},
  labelCustomStyle = {},
  isLogin = false,
  ...rest
}: CheckboxProps): ReactElement => {
  const [focused, setFocused] = useState(false);

  const focusHandler = () => {
    setFocused(true);
  };

  const blurHandler = () => {
    setFocused(false);
  };

  return (
    <Label inline={inline} isLogin={isLogin} noSpace={noSpace} customStyle={labelCustomStyle}>
      <CheckboxInput onFocus={focusHandler} onBlur={blurHandler} value={value} {...rest} />
      <StyledCheckbox hasError={hasError} focused={focused} checked={value} customStyle={customStyle}>
        {value && (
          <CheckmarkContainer>
            <Checkmark customStyle={svgCustomStyle} />
          </CheckmarkContainer>
        )}
      </StyledCheckbox>
      <LabelText disabled={disabled} size={size} customLabelTextStyle={customLabelTextStyle}>
        {label}
      </LabelText>
    </Label>
  );
};

Checkbox.defaultProps = {
  size: 'SM',
  hasError: false,
};

Checkbox.propTypes = {
  /** Determines size of font SM (default): 14px, MD: 16px */
  size: PropTypes.oneOf(['SM', 'MD']),

  onChange: PropTypes.func,
  /** Label next to checkbox */
  label: PropTypes.any,
  /** Determines if checkbox should display inline-block */
  inline: PropTypes.bool,
  /** Determines if checkbox should have red borders */
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  type: PropTypes.string,
};

export default Checkbox;
